import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { adminAuth } from "@/lib/adminAuth";
import { BrandHeader } from "@/components/BrandHeader";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = useMutation({
    mutationFn: () => adminAuth.resetPassword(token, password),
    onSuccess: () => { setDone(true); setError(null); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Reset failed."),
  });

  const validate = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) { setError("Missing reset token in the URL."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    submit.mutate();
  };

  return (
    <>
      <BrandHeader />
      <main className="grid min-h-[calc(100vh-80px)] place-items-center bg-brand-light p-6">
        <div className="card w-full max-w-sm p-8">
          <h1 className="text-2xl font-bold text-brand">Reset password</h1>

          {done ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800">
                ✓ Your password has been updated. You can sign in now.
              </div>
              <button className="btn-primary w-full justify-center"
                onClick={() => navigate("/admin/login", { replace: true })}>
                Go to sign in
              </button>
            </div>
          ) : !token ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg bg-brand-light p-4 text-sm text-brand">
                This reset link is missing its token. Request a new link from the forgot-password page.
              </div>
              <Link to="/admin/forgot-password" className="btn-primary w-full justify-center">
                Request a new link
              </Link>
            </div>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={validate}>
              <div>
                <label className="field-label" htmlFor="pw">New password</label>
                <input id="pw" type="password" required minLength={8}
                  autoComplete="new-password" autoFocus
                  className="field-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} />
                <p className="mt-1 text-xs text-brand-ink/60">At least 8 characters.</p>
              </div>
              <div>
                <label className="field-label" htmlFor="cf">Confirm new password</label>
                <input id="cf" type="password" required minLength={8}
                  autoComplete="new-password"
                  className="field-input" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} />
              </div>

              {error && <div className="text-sm text-brand" role="alert">{error}</div>}

              <button type="submit" className="btn-primary w-full justify-center"
                disabled={submit.isPending}>
                {submit.isPending ? "Updating…" : "Update password"}
              </button>
              <div className="text-center text-sm">
                <Link to="/admin/login" className="text-brand hover:underline">← Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </>
  );
}
